import { useState, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  ShieldAlert, Send, Search, Eye, MessageCircle, Lock
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const STATUS_BADGES = { received: "bg-blue-100 text-blue-700", investigating: "bg-amber-100 text-amber-700", resolved: "bg-emerald-100 text-emerald-700", dismissed: "bg-slate-100 text-slate-600" };

const Complaints = () => {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin_empresa", "gerente_rh"].includes(user?.role);
  const [tab, setTab] = useState(isAdmin ? "manage" : "submit");
  const [complaints, setComplaints] = useState([]);
  const [loadedComplaints, setLoadedComplaints] = useState(false);
  const [newComplaint, setNewComplaint] = useState({ description: "", category: "acoso", is_anonymous: true });
  const [trackId, setTrackId] = useState("");
  const [trackPin, setTrackPin] = useState("");
  const [trackedComplaint, setTrackedComplaint] = useState(null);
  const [submittedInfo, setSubmittedInfo] = useState(null);

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchComplaints = async () => {
    try {
      const res = await axios.get(`${API}/complaints`, { headers: headers(), withCredentials: true });
      setComplaints(res.data || []);
      setLoadedComplaints(true);
    } catch { toast.error("Error al cargar denuncias"); }
  };

  const handleSubmit = async () => {
    if (!newComplaint.description) { toast.error("Descripción requerida"); return; }
    try {
      const companyId = user?.company_id || "";
      const res = await axios.post(`${API}/complaints/public?company_id=${companyId}`, newComplaint);
      setSubmittedInfo(res.data);
      setNewComplaint({ description: "", category: "acoso", is_anonymous: true });
      toast.success("Denuncia enviada exitosamente");
    } catch { toast.error("Error al enviar denuncia"); }
  };

  const handleTrack = async () => {
    if (!trackId || !trackPin) { toast.error("ID y PIN requeridos"); return; }
    try {
      const res = await axios.get(`${API}/complaints/track?complaint_id=${trackId}&pin=${trackPin}`);
      setTrackedComplaint(res.data);
    } catch { toast.error("Denuncia no encontrada o PIN incorrecto"); }
  };

  const handleStatusChange = async (complaintId, status) => {
    try {
      await axios.put(`${API}/complaints/${complaintId}/status?status=${status}`, {}, { headers: headers(), withCredentials: true });
      toast.success("Estado actualizado");
      fetchComplaints();
    } catch { toast.error("Error al actualizar"); }
  };

  return (
    <Layout title="Canal de Denuncias" subtitle="Reportes confidenciales y anónimos">
      <Tabs value={tab} onValueChange={v => { setTab(v); if (v === "manage" && !loadedComplaints) fetchComplaints(); }}>
        <TabsList className="mb-6" data-testid="complaints-tabs">
          <TabsTrigger value="submit" data-testid="tab-submit"><Send className="w-4 h-4 mr-2" /> Enviar Denuncia</TabsTrigger>
          <TabsTrigger value="track" data-testid="tab-track"><Search className="w-4 h-4 mr-2" /> Dar Seguimiento</TabsTrigger>
          {isAdmin && <TabsTrigger value="manage" data-testid="tab-manage"><Eye className="w-4 h-4 mr-2" /> Gestionar</TabsTrigger>}
        </TabsList>

        <TabsContent value="submit">
          <div className="max-w-lg mx-auto">
            {submittedInfo ? (
              <Card data-testid="submitted-info">
                <CardContent className="p-6 text-center">
                  <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Denuncia Enviada</h3>
                  <p className="text-sm text-slate-600 mb-4">Guarda estos datos para dar seguimiento:</p>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div><span className="text-sm text-slate-500">ID:</span> <span className="font-mono font-bold">{submittedInfo.complaint_id}</span></div>
                    <div><span className="text-sm text-slate-500">PIN:</span> <span className="font-mono font-bold">{submittedInfo.pin}</span></div>
                  </div>
                  <Button className="mt-4" onClick={() => setSubmittedInfo(null)} data-testid="new-complaint-btn">Nueva Denuncia</Button>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="complaint-form">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><Lock className="w-4 h-4 text-emerald-600" /> Enviar Denuncia Confidencial</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>Categoría</Label>
                    <select className="form-input w-full mt-1" value={newComplaint.category} onChange={e => setNewComplaint({ ...newComplaint, category: e.target.value })}>
                      <option value="acoso">Acoso</option><option value="discriminacion">Discriminación</option>
                      <option value="fraude">Fraude</option><option value="seguridad">Seguridad</option>
                      <option value="etica">Ética</option><option value="otro">Otro</option>
                    </select>
                  </div>
                  <div><Label>Descripción</Label><Textarea rows={5} value={newComplaint.description} onChange={e => setNewComplaint({ ...newComplaint, description: e.target.value })} placeholder="Describe la situación con el mayor detalle posible..." data-testid="complaint-desc" /></div>
                  <div className="flex items-center gap-3">
                    <Switch checked={newComplaint.is_anonymous} onCheckedChange={v => setNewComplaint({ ...newComplaint, is_anonymous: v })} />
                    <Label>Enviar de forma anónima</Label>
                  </div>
                  <Button onClick={handleSubmit} className="w-full" data-testid="submit-complaint-btn">
                    <ShieldAlert className="w-4 h-4 mr-2" /> Enviar Denuncia
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="track">
          <div className="max-w-md mx-auto space-y-4">
            <Card data-testid="track-form">
              <CardHeader><CardTitle className="text-base">Seguimiento de Denuncia</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>ID de Denuncia</Label><Input value={trackId} onChange={e => setTrackId(e.target.value)} placeholder="compl_..." data-testid="track-id" /></div>
                <div><Label>PIN</Label><Input value={trackPin} onChange={e => setTrackPin(e.target.value)} placeholder="Tu PIN de 6 caracteres" data-testid="track-pin" /></div>
                <Button onClick={handleTrack} className="w-full" data-testid="track-btn"><Search className="w-4 h-4 mr-2" /> Buscar</Button>
              </CardContent>
            </Card>
            {trackedComplaint && (
              <Card data-testid="tracked-result">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">Estado</p>
                    <Badge className={STATUS_BADGES[trackedComplaint.status]}>{trackedComplaint.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{trackedComplaint.description}</p>
                  {(trackedComplaint.messages || []).map((m, i) => (
                    <div key={i} className="bg-slate-50 rounded p-2 text-sm"><p>{m.content}</p><p className="text-xs text-slate-400">{m.created_at?.slice(0, 16)}</p></div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="manage">
            {complaints.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-slate-500" data-testid="complaints-empty">
                <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay denuncias</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {complaints.map(c => (
                  <Card key={c.complaint_id} data-testid={`complaint-${c.complaint_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{c.category}</Badge>
                            <Badge className={STATUS_BADGES[c.status]}>{c.status}</Badge>
                            {c.is_anonymous && <Badge className="bg-slate-100 text-slate-600">Anónima</Badge>}
                          </div>
                          <p className="text-sm text-slate-700">{c.description}</p>
                          <p className="text-xs text-slate-400 mt-1">{c.created_at?.slice(0, 16)}</p>
                        </div>
                        <div className="flex gap-1">
                          {c.status === "received" && <Button size="sm" variant="outline" onClick={() => handleStatusChange(c.complaint_id, "investigating")}>Investigar</Button>}
                          {c.status === "investigating" && <Button size="sm" onClick={() => handleStatusChange(c.complaint_id, "resolved")}>Resolver</Button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </Layout>
  );
};

export default Complaints;
